import { Icon, IconName, IconSize } from '@ardoq/icons';
import { KeyboardEvent, MouseEvent, PropsWithChildren, ReactNode } from 'react';
import { StatusType, Tag } from '@ardoq/status-ui';
import { PlainTextPopover, WithUncontrolledPopover } from '@ardoq/popover2025';
import {
  IslandHeaderBody,
  IslandHeaderSubtitle,
  IslandHeaderTagsWrapper,
  IslandHeaderTitle,
  IslandHeaderTitlePlaceholder,
  IslandHeaderWrapper,
  SubtitleWidth,
} from './atoms';
import { colors, s16, r8 } from '@ardoq/design-tokens';
import styled from 'styled-components';
import { Spacing } from './IslandSectionContainer';
import { FlexBox } from '@ardoq/layout';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

export type IslandHeaderProps = {
  title?: string | ReactNode;
  titlePlaceholder?: string;
  titleAddon?: ReactNode;
  subtitle?: ReactNode;
  subtitleWidth?: SubtitleWidth;
  tags?: ReactNode;
  errorMessage?: string;
  warningMessage?: string;
  iconName?: IconName;
  knowledgeHint?: string;
  rightContent?: ReactNode;
  skipVerticalPadding?: boolean;
  barColor?: string;
  skipVerticalAlign?: boolean;
  knowledgeBaseLink?: string;
  showHeaderBorder?: boolean;
  headerSpacing?: Spacing;
  /*
   * This prop prevents the default behavior of overriding the title icon color and margin
   */
  preventTitleIconChange?: boolean;
  onHeaderClicked?: (event: MouseEvent | KeyboardEvent) => void;
  i18nLocale?: I18nLocale;
};

export const ColorBar = styled.div.attrs<{ $barColor: string }>(
  ({ $barColor }) => ({
    style: { background: $barColor },
  })
)`
  width: 4px;
  border-radius: 8px;
  margin: ${s16} 0 ${s16} ${s16};
  float: left;
`;

export const COLOR_BAR_SELECTOR = ColorBar.toString();

const BorderWrapper = styled.div<{
  $clickable?: boolean;
  $showHeaderBorder: boolean;
}>`
  display: flex;
  ${({ $showHeaderBorder }) =>
    $showHeaderBorder && `border-bottom: 1px solid ${colors.borderAction};`}
  &:only-child {
    border-bottom: 0;
  }
  border-radius: ${r8};
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover {
    background-color: ${({ $clickable }) =>
      $clickable ? colors.surfaceActionGhostHover : 'none'};
  }
`;

export const IslandHeader = ({
  title,
  titlePlaceholder,
  titleAddon,
  subtitle,
  subtitleWidth,
  tags,
  errorMessage,
  warningMessage,
  iconName,
  knowledgeHint,
  rightContent,
  children,
  skipVerticalPadding,
  skipVerticalAlign,
  barColor,
  knowledgeBaseLink,
  showHeaderBorder = true,
  headerSpacing,
  preventTitleIconChange,
  onHeaderClicked,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: PropsWithChildren<IslandHeaderProps>) => {
  const shouldRenderHeader = Boolean(
    title || titlePlaceholder || subtitle || tags || errorMessage || children
  );
  if (!shouldRenderHeader) {
    return null;
  }
  const clickable = Boolean(onHeaderClicked);
  return (
    <BorderWrapper
      $clickable={clickable}
      onClick={onHeaderClicked}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === ' ' || e.key === 'Enter' || e.key === 'Spacebar') {
          onHeaderClicked?.(e);
        }
      }}
      data-test-id="collapsible-island-header"
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? 'false' : undefined}
      $showHeaderBorder={showHeaderBorder}
    >
      {barColor && <ColorBar $barColor={barColor} />}
      <IslandHeaderWrapper
        $skipVerticalPadding={skipVerticalPadding}
        $hasColorBar={Boolean(barColor)}
        $spacing={headerSpacing}
      >
        <FlexBox
          gap="medium"
          {...(skipVerticalAlign ? {} : { align: 'center' })}
        >
          <IslandHeaderBody>
            <IslandHeaderTagsWrapper>
              {errorMessage ? (
                <Tag statusType={StatusType.ERROR}>{errorMessage}</Tag>
              ) : warningMessage ? (
                <Tag statusType={StatusType.WARNING}>{warningMessage}</Tag>
              ) : null}
              {tags}
            </IslandHeaderTagsWrapper>
            {!title && !!titlePlaceholder && (
              <IslandHeaderTitlePlaceholder>
                {titlePlaceholder}
              </IslandHeaderTitlePlaceholder>
            )}
            {title && (
              <FlexBox gap="xsmall">
                <IslandHeaderTitle $preventIconChange={preventTitleIconChange}>
                  {iconName && <Icon iconName={iconName} />}
                  <span role="heading">
                    {typeof title === 'string' && !title.trim() ? '\0' : title}
                  </span>
                  {(knowledgeBaseLink || knowledgeHint) && (
                    <WithUncontrolledPopover
                      content={
                        knowledgeHint ? (
                          <PlainTextPopover>{knowledgeHint}</PlainTextPopover>
                        ) : null
                      }
                    >
                      <Icon
                        iconName={IconName.HELP}
                        iconSize={IconSize.MEDIUM}
                        {...(!knowledgeHint
                          ? {
                              'data-tooltip-text': t(
                                i18nLocale,
                                'pageLayout:islandHeader.knowledgeBase',
                                'Knowledge base'
                              ),
                            }
                          : {})}
                        deprecated_onClick={() =>
                          knowledgeBaseLink &&
                          window.open(knowledgeBaseLink, '_blank noreferrer')
                        }
                        style={{
                          cursor: 'pointer',
                        }}
                      />
                    </WithUncontrolledPopover>
                  )}
                </IslandHeaderTitle>
                {titleAddon}
              </FlexBox>
            )}
            {subtitle && (
              <IslandHeaderSubtitle $subtitleWidth={subtitleWidth}>
                {subtitle}
              </IslandHeaderSubtitle>
            )}
          </IslandHeaderBody>
          {rightContent}
        </FlexBox>
        {children}
      </IslandHeaderWrapper>
    </BorderWrapper>
  );
};
